import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import "./DashboardCard.css";
import "./YourEndeavors.css"; // TODO migrate styles
import '../primitives/Button.css';
import '../primitives/Stack.css';
import '../primitives/Card.css';
import EndeavorCard from './EndeavorCard'; // ✅ use the compact card
import useIsMobile from '../../hooks/useIsMobile';
import AddNewButton from '../common/AddNewButton';

function YourEndeavors({ onCountUpdate }) {
  const { user } = useAuth();
  const { getFilteredEndeavors, isLoading } = useData();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [endeavors, setEndeavors] = useState([]);
  const [error, setError] = useState(null);

  const userId = user?.id || user?.userId;
  const loading = isLoading(`endeavors_user-${userId}`);

  /* ==== LOOP FIX HELPERS ==== */
  // Keep latest onCountUpdate without making it a dependency that recreates callbacks
  const onCountUpdateRef = useRef(onCountUpdate);
  useEffect(()=>{ onCountUpdateRef.current = onCountUpdate; }, [onCountUpdate]);

  // Only fire count callback when the numeric value actually changes
  const lastCountRef = useRef(null);
  const emitCountIfChanged = useCallback((count) => {
    if(onCountUpdateRef.current && lastCountRef.current !== count){
      lastCountRef.current = count;
      onCountUpdateRef.current(count);
    }
  }, []);

  const fetchInFlightRef = useRef(false);
  
  // ✅ PHASE 3: Stabilize getFilteredEndeavors reference to prevent callback recreation
  const getFilteredEndeavorsRef = useRef(getFilteredEndeavors);
  useEffect(() => { getFilteredEndeavorsRef.current = getFilteredEndeavors; }, [getFilteredEndeavors]);

  const fetchEndeavors = useCallback(async () => {
    if (!userId) return;
    if (fetchInFlightRef.current) return; // guard against overlapping re-entries
    fetchInFlightRef.current = true;
    try {
      setError(null);
      console.log('YourEndeavors: Fetching endeavors for user:', userId);
      const endeavorsList = await getFilteredEndeavorsRef.current(userId);
      console.log('YourEndeavors: Received data:', endeavorsList);
      // Only update state if content actually differs (prevents useless re-renders)
      const newSig = endeavorsList.map(e => e.endeavorId || e.id).join('|');
      if (newSig !== fetchEndeavors._lastSig) {
        fetchEndeavors._lastSig = newSig;
        setEndeavors(endeavorsList);
        emitCountIfChanged(endeavorsList.length);
      } else {
        // Still ensure count emitted once if identical and not yet emitted
        emitCountIfChanged(endeavorsList.length);
      }
    } catch (err) {
      console.error('YourEndeavors: Error fetching endeavors:', err);
      setError(err);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [userId, emitCountIfChanged]); // ✅ PHASE 3: Removed getFilteredEndeavors dependency (uses ref)

  useEffect(() => {
    if (userId) {
      fetchEndeavors();
    }
  }, [userId, fetchEndeavors]);

  // If endeavors array changes from other internal actions, emit count (signature guard avoids loops)
  useEffect(() => {
    emitCountIfChanged(endeavors.length);
  }, [endeavors, emitCountIfChanged]);

  const handleRefresh = async () => {
    await fetchEndeavors();
  };

  const handleEndeavorClick = (endeavor) => {
    const endeavorId = endeavor.endeavorId || endeavor.id;
    if (!endeavorId) return;
    navigate(isMobile ? `/m/course/${endeavorId}` : `/course/${endeavorId}`);
  };

  const handleCreateNew = () => {
    console.log('Create new endeavor clicked - navigating to wizard');
  navigate(isMobile ? '/m/create-course-wizard' : '/create-course-wizard');
  };

  return (
    <div className={`dashboard-card your-endeavors-card ${isMobile ? 'full-height' : ''}`}>
      {/* ✅ Header without leading icon */}
      <div className="card-main-header">
        <div className="card-title-with-counts">
          <h2 className="card-main-title">
            {/* Removed icon span */}
            Courses
          </h2>
          <div className="card-counts">
            {loading ? (
              <span className="count-text">Loading...</span>
            ) : (
              <span className="count-text">
                {endeavors.length} {endeavors.length === 1 ? 'course' : 'courses'}
              </span>
            )}
          </div>
        </div>
        <div className="card-header-actions">
          <AddNewButton onClick={handleCreateNew} disabled={loading} ariaLabel="Create new course" />
          {/* Removed refresh button */}
        </div>
      </div>

      {/* ✅ REMOVED: card-summary-header section */}
      
      <div className="card-content endeavors-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading courses...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>Failed to load courses: {error.message}</p>
            <button type="button" className="btn btn-secondary" onClick={handleRefresh}>🔄 Try Again</button>
          </div>
        ) : endeavors.length === 0 ? (
          <div className="empty-state celebration-empty">
            <div className="mascot-container">
              <div className="mascot">📚</div>
              <div className="mascot-speech">
                <div className="speech-bubble">
                  Let's get started!
                </div>
              </div>
            </div>
            <div className="celebration-content">
              <h3>Ready to begin your learning journey?</h3>
              <p>Create your first course to start organizing your academic life!</p>
              <div className="celebration-actions">
                <AddNewButton onClick={handleCreateNew} ariaLabel="Create new course" />
              </div>
            </div>
          </div>
        ) : (
          <div className="endeavors-scroller">
            {/* Removed internal Create New button section */}
            <div className="endeavors-list">
              {endeavors.map((endeavor) => {
                const endeavorId = endeavor.endeavorId || endeavor.id;
                return (
                  <EndeavorCard
                    key={endeavorId}
                    endeavor={endeavor}
                    onClick={() => handleEndeavorClick(endeavor)}
                  />
                );
              })}
            </div>
            {endeavors.length > 3 && (
              <div className="list-footer">
                {endeavors.length} total courses
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default YourEndeavors;